/** Normalized email pattern (after {@link sanitizeEmail}). */
export const FIELD_EMAIL_REGEX = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

export const FIELD_EMAIL_INVALID_MESSAGE = 'Please enter a valid email address.';

/**
 * Rejects common invalid local-part / domain shapes that {@link FIELD_EMAIL_REGEX} still allows.
 * Returns an error message, or empty string when structure is acceptable (or still incomplete).
 */
export function getEmailStructuralError(sanitized: string): string {
  const at = sanitized.indexOf('@');
  if (at <= 0 || at >= sanitized.length - 1) {
    return '';
  }

  const local = sanitized.slice(0, at);
  const domain = sanitized.slice(at + 1);

  if (local.includes('..') || local.startsWith('.') || local.endsWith('.')) {
    return FIELD_EMAIL_INVALID_MESSAGE;
  }

  if (domain.includes('..') || domain.startsWith('.') || domain.startsWith('-')) {
    return FIELD_EMAIL_INVALID_MESSAGE;
  }

  const labels = domain.split('.');
  for (const label of labels) {
    if (!label || label.startsWith('-') || label.endsWith('-')) {
      return FIELD_EMAIL_INVALID_MESSAGE;
    }
  }

  return '';
}

/** Allowed characters while typing; empty is allowed. */
export const FIELD_NAME_ALLOWED_REGEX = /^[A-Za-z0-9_\s'.,-]*$/;

export const FIELD_NAME_MAX_LENGTH = 100;

export const FIELD_NAME_FORMAT_ERROR =
  'Only alphanumeric characters, hyphens, underscores, spaces, apostrophes, dots, and commas are allowed.';

/**
 * Trim ends, remove internal whitespace, lowercase — suitable for login / invite style emails.
 */
export function sanitizeEmail(raw: string | null | undefined): string {
  if (raw == null || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const collapsed = trimmed.replace(/\s+/g, '');
  return collapsed.toLowerCase();
}

/** Trim ends; collapse internal whitespace runs to a single space. */
export function sanitizeName(raw: string | null | undefined): string {
  if (raw == null || typeof raw !== 'string') return '';
  return raw.trim().replace(/\s+/g, ' ');
}

export interface FieldValidationResult {
  sanitized: string;
  error: string;
}

export function validateEmail(value: string | null | undefined, required: boolean): FieldValidationResult {
  const sanitized = sanitizeEmail(value);
  if (!sanitized) {
    return { sanitized, error: required ? 'Email is required.' : '' };
  }
  const structuralError = getEmailStructuralError(sanitized);
  if (structuralError) {
    return { sanitized, error: structuralError };
  }
  if (!FIELD_EMAIL_REGEX.test(sanitized)) {
    return { sanitized, error: FIELD_EMAIL_INVALID_MESSAGE };
  }
  return { sanitized, error: '' };
}

/** Characters that may appear anywhere in a sanitized email address. */
const FIELD_EMAIL_TYPED_CHARS_REGEX = /^[a-z0-9._%+@-]*$/;

/**
 * While typing or pasting: report only the problems that typing more characters can never fix
 * (illegal characters, a second `@`, consecutive or misplaced dots). Input that is merely
 * incomplete, such as `user@gmail`, returns '' and is caught by {@link validateEmail} on
 * blur/submit.
 */
export function emailTypingError(value: string | null | undefined): string {
  const s = sanitizeEmail(value);
  if (!s) {
    return '';
  }
  if (!FIELD_EMAIL_TYPED_CHARS_REGEX.test(s)) {
    return FIELD_EMAIL_INVALID_MESSAGE;
  }
  if (s.indexOf('@') !== s.lastIndexOf('@')) {
    return FIELD_EMAIL_INVALID_MESSAGE;
  }
  // A trailing dot can still be mid-typing (`user@gmail.`), so it is not an error yet.
  return getEmailStructuralError(s.endsWith('.') ? s.slice(0, -1) : s);
}

/** While typing: invalid format only (not required until blur/submit). Uses {@link sanitizeEmail}, structural checks, then {@link FIELD_EMAIL_REGEX}. */
export function emailLiveError(value: string | null | undefined): string {
  const s = sanitizeEmail(value);
  if (!s) {
    return '';
  }
  const structuralError = getEmailStructuralError(s);
  if (structuralError) {
    return structuralError;
  }
  if (!FIELD_EMAIL_REGEX.test(s)) {
    return FIELD_EMAIL_INVALID_MESSAGE;
  }
  return '';
}

export function validateName(
  value: string | null | undefined,
  fieldLabel: string,
  required: boolean,
): FieldValidationResult {
  const sanitized = sanitizeName(value);
  if (!sanitized.trim()) {
    return { sanitized, error: required ? `${fieldLabel} is required.` : '' };
  }
  if (!FIELD_NAME_ALLOWED_REGEX.test(sanitized)) {
    return { sanitized, error: FIELD_NAME_FORMAT_ERROR };
  }
  if (sanitized.length > FIELD_NAME_MAX_LENGTH) {
    return {
      sanitized,
      error: `${fieldLabel} must be at most ${FIELD_NAME_MAX_LENGTH} characters.`,
    };
  }
  return { sanitized, error: '' };
}

/** While typing: show format/length issues only (not required-until-blur). */
export function nameLiveError(value: string | null | undefined): string {
  const v = value ?? '';
  if (v && !FIELD_NAME_ALLOWED_REGEX.test(v)) {
    return FIELD_NAME_FORMAT_ERROR;
  }
  if (v.length > FIELD_NAME_MAX_LENGTH) {
    return `Must be at most ${FIELD_NAME_MAX_LENGTH} characters.`;
  }
  return '';
}
